/**
 * @fileoverview Candidacy Residents Section Component
 * 
 * Displays the residents table for a candidacy
 */

import { useState } from "react";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "components/ui/table";
import { Button } from "components/ui/button";
import { Modal, ModalContent, ModalHeader, ModalBody, Spinner, Image } from "@heroui/react";
import { UsersThree, IdentificationCard, Receipt } from "@phosphor-icons/react";
import { format } from "date-fns";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";

interface CandidacyResidentsSectionProps {
  entity: any;
}

export const CandidacyResidentsSection: React.FC<CandidacyResidentsSectionProps> = ({ entity }) => {
  const { session } = useSession();
  const [documentPreview, setDocumentPreview] = useState<{ fileType: string; image: string }[]>([]);
  const [documentPreviewOpen, setDocumentPreviewOpen] = useState<boolean>(false);
  const [documentPreviewTitle, setDocumentPreviewTitle] = useState<string>("Documenten");
  const [isDocumentPreviewLoading, setIsDocumentPreviewLoading] = useState<boolean>(false);
  const [expandedDocument, setExpandedDocument] = useState<{ fileType: string; image: string; index: number } | null>(null);

  if (!entity?.residents || entity.residents.length === 0) return null;

  const fetchResidentImages = async (residentId: string, imageTypes: string[], title?: string) => {
    setDocumentPreview([]);
    setDocumentPreviewTitle(title || "Documenten");
    setIsDocumentPreviewLoading(true);

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/get-resident-image/${residentId}?imageType=${imageTypes.join(',')}`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const images = await response.json();
        setDocumentPreview(Array.isArray(images) ? images : []);
      } else {
        console.error('Failed to fetch images, response not ok:', response.status);
      }
    } catch (error) {
      console.error("Error fetching resident images:", error);
      setDocumentPreview([]);
    } finally {
      setIsDocumentPreviewLoading(false);
    }
  };

  const handleViewIdDocuments = (resident: any) => {
    const imageTypes = ['idFront', 'idBack'];
    const fullName = `${resident.firstName || ''} ${resident.lastName || ''}`.trim();
    const title = fullName ? `Identiteitskaart ${fullName}` : 'Identiteitskaart';
    fetchResidentImages(resident.id, imageTypes, title);
    setExpandedDocument(null);
    setDocumentPreviewOpen(true);
  };

  const handleViewIncomeDocuments = (resident: any) => {
    const imageTypes = ['incomeMonth'];
    const fullName = `${resident.firstName || ''} ${resident.lastName || ''}`.trim();
    const title = fullName ? `Loonfiche ${fullName}` : 'Loonfiche';
    fetchResidentImages(resident.id, imageTypes, title);
    setExpandedDocument(null);
    setDocumentPreviewOpen(true);
  };

  // Check if resident has ID documents
  const hasIdDocuments = (resident: any) => {
    return resident.idFront || resident.idBack;
  };

  // Check if resident has income documents
  const hasIncomeDocuments = (resident: any) => {
    return resident.incomeMonth1 || resident.incomeMonth2 || resident.incomeMonth3;
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-neutral-primary/20 shadow-sm overflow-hidden">
        <div className="border-b border-neutral-primary/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <UsersThree size={18} className="text-moss-primary" weight="duotone" />
            <h3 className="text-base font-semibold text-neutral-primary">
              Bewoners ({entity.residents.length})
            </h3>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Geboortedatum</TableHead>
                <TableHead>Nationaliteit</TableHead>
                <TableHead>Relatie</TableHead>
                <TableHead>Loonfiche</TableHead>
                <TableHead>Identiteitskaart</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entity.residents.map((resident: any, index: number) => (
                <TableRow key={index}>
                  <TableCell>
                    {resident.firstName} {resident.lastName}
                  </TableCell>
                  <TableCell>
                    {resident.dateOfBirth 
                      ? format(new Date(resident.dateOfBirth), 'dd/MM/yyyy')
                      : '-'
                    }
                  </TableCell>
                  <TableCell>{resident.nationality || '-'}</TableCell>
                  <TableCell>{resident.relationToApplicant || '-'}</TableCell>
                  <TableCell>
                    {hasIncomeDocuments(resident) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewIncomeDocuments(resident)}
                        className="bg-moss-primary text-white hover:bg-moss-primary/90 border-moss-primary"
                      >
                        <Receipt size={16} className="mr-2" weight="duotone" />
                        Bekijk loonfiche
                      </Button>
                    ) : (
                      <span className="text-sm text-neutral-secondary-mid">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {hasIdDocuments(resident) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewIdDocuments(resident)}
                        className="bg-moss-primary text-white hover:bg-moss-primary/90 border-moss-primary"
                      >
                        <IdentificationCard size={16} className="mr-2" weight="duotone" />
                        Bekijk identiteitskaart
                      </Button>
                    ) : (
                      <span className="text-sm text-neutral-secondary-mid">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Modal
        isOpen={documentPreviewOpen}
        onClose={() => {
          setDocumentPreviewOpen(false);
          setExpandedDocument(null);
        }}
        backdrop="blur"
        size="5xl"
        className="p-4"
      >
        <ModalContent>
          <ModalHeader className="flex items-center justify-between text-base font-semibold">
            <span>{documentPreviewTitle}</span>
          </ModalHeader>
          <ModalBody className="p-6">
            {isDocumentPreviewLoading ? (
              <div className="flex justify-center py-10">
                <Spinner color="success" />
              </div>
            ) : documentPreview.length > 0 ? (
              <div className="flex flex-row gap-4 overflow-x-auto max-h-[70vh]">
                {documentPreview.map((doc, index) => {
                  const fileType = (doc.fileType || '').toLowerCase();
                  return (
                    <div key={`${doc.fileType}-${index}`} className="flex flex-col gap-2">
                      <div
                        className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => setExpandedDocument({ ...doc, index })}
                      >
                        {fileType === 'pdf' ? (
                          <div className="relative group">
                            <iframe
                              src={`data:application/pdf;base64,${doc.image}`}
                              className="w-full h-96 object-contain pointer-events-none"
                              title={`${documentPreviewTitle} ${index + 1}`}
                            />
                            <div className="absolute inset-0 bg-transparent group-hover:bg-black group-hover:bg-opacity-10 transition-all" />
                            <div className="absolute top-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded-sm text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Klik om te vergroten
                            </div>
                          </div>
                        ) : (
                          <div className="relative group">
                            <Image
                              src={`data:image/${doc.fileType};base64,${doc.image}`}
                              className="w-full h-auto object-contain"
                              alt={`${documentPreviewTitle} ${index + 1}`}
                            />
                            <div className="absolute inset-0 bg-transparent group-hover:bg-black group-hover:bg-opacity-10 transition-all" />
                            <div className="absolute top-2 right-2 bg-white bg-opacity-90 px-2 py-1 rounded-sm text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              Klik om te vergroten
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-neutral-secondary-mid text-center py-12">
                Geen documenten gevonden
              </p>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={Boolean(expandedDocument)}
        onClose={() => setExpandedDocument(null)}
        size="full"
        backdrop="blur"
        className="p-4"
      >
        <ModalContent>
          <ModalHeader className="flex justify-between items-center">
            <span>
              {documentPreviewTitle} {expandedDocument ? expandedDocument.index + 1 : ''} - {expandedDocument?.fileType?.toUpperCase()}
            </span>
          </ModalHeader>
          <ModalBody className="p-6 flex items-center justify-center">
            {expandedDocument && (
              <div className="w-full h-full flex items-center justify-center">
                {(expandedDocument.fileType || '').toLowerCase() === 'pdf' ? (
                  <iframe
                    src={`data:application/pdf;base64,${expandedDocument.image}`}
                    className="w-full h-[80vh] border rounded-lg"
                    title={`${documentPreviewTitle} ${expandedDocument.index + 1}`}
                  />
                ) : (
                  <Image
                    src={`data:image/${expandedDocument.fileType};base64,${expandedDocument.image}`}
                    className="max-w-full max-h-[80vh] object-contain rounded-lg border"
                    alt={`${documentPreviewTitle} ${expandedDocument.index + 1}`}
                  />
                )}
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};
